import { useState, useEffect } from 'react';
import { Loader2, MousePointer, TrendingUp, Globe, Activity, BarChart3, Trash2, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AnalyticsTab = ({ headers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [view, setView] = useState('posthog');
  const [phData, setPhData] = useState(null);
  const [phLoading, setPhLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/analytics/dashboard`, { headers, credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error('Failed to fetch analytics:', e); }
    finally { setLoading(false); }
  };

  const fetchPostHog = async () => {
    setPhLoading(true);
    try {
      const res = await fetch(`${API}/api/posthog/dashboard`, { headers, credentials: 'include' });
      if (res.ok) setPhData(await res.json());
    } catch (e) { console.error('Failed to fetch PostHog data:', e); }
    finally { setPhLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchData(); fetchPostHog(); }, []);

  const handleReset = async () => {
    if (!window.confirm('PERMANENTLY delete all analytics data? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? All page views, sessions, and funnel data will be erased.')) return;
    setResetting(true);
    try {
      const res = await fetch(`${API}/api/admin/analytics/reset`, {
        method: 'DELETE', headers, credentials: 'include',
      });
      if (res.ok) {
        await fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to reset analytics');
      }
    } catch (e) { alert('Reset failed'); }
    finally { setResetting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-12">Unable to load analytics data.</p>;

  const metricCards = [
    { label: 'Page Views (24h)', value: data.page_views.last_24h, icon: MousePointer, color: '#0066CC' },
    { label: 'Page Views (7d)', value: data.page_views.last_7d, icon: TrendingUp, color: '#14b8a6' },
    { label: 'Page Views (30d)', value: data.page_views.last_30d, icon: Globe, color: '#8b5cf6' },
    { label: 'Sessions (24h)', value: data.sessions.last_24h, icon: Activity, color: '#f59e0b' },
    { label: 'Sessions (7d)', value: data.sessions.last_7d, icon: Activity, color: '#10b981' },
    { label: 'Avg Pages/Session', value: data.engagement?.avg_pages_per_session || 0, icon: BarChart3, color: '#ef4444' },
  ];

  const funnel = data.conversion_funnel || {};
  const funnelSteps = [
    { label: 'Visitors (30d)', value: funnel.visitors || 0, color: '#0066CC' },
    { label: 'Registered', value: funnel.registered_users || 0, color: '#14b8a6' },
    { label: 'Diagnostics', value: funnel.diagnostics || 0, color: '#8b5cf6' },
    { label: 'Leads', value: funnel.leads || 0, color: '#f59e0b' },
    { label: 'Business Cases', value: funnel.business_cases || 0, color: '#10b981' },
    { label: 'Contacts', value: funnel.contacts || 0, color: '#ef4444' },
  ];
  const maxFunnel = Math.max(...funnelSteps.map(f => f.value), 1);

  const heatmapData = data.hourly_heatmap || [];
  const uniqueDays = [...new Set(heatmapData.map(h => h.day))].sort().slice(-7);
  const maxHeatCount = Math.max(...heatmapData.map(h => h.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between gap-4">
        <div className="flex gap-2">
          {['posthog', 'overview', 'funnel', 'heatmap'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                view === v ? 'bg-taxera-blue text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`analytics-view-${v}`}>
              {v === 'posthog' ? 'Visitor Insights' : v === 'overview' ? 'Overview' : v === 'funnel' ? 'Conversion Funnel' : 'Traffic Heatmap'}
            </button>
          ))}
        </div>
        <button
          onClick={handleReset}
          disabled={resetting}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-50"
          data-testid="analytics-reset-btn"
        >
          {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
          Reset Analytics
        </button>
      </div>

      {view === 'posthog' && (
        <div className="space-y-6" data-testid="posthog-insights">
          {phLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>
          ) : !phData ? (
            <p className="text-sm text-slate-400 text-center py-12">PostHog data not available. Ensure PostHog is configured and events are being tracked.</p>
          ) : (
            <>
              {/* Key metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Unique Visitors (7d)', value: phData.unique_visitors_7d || 0, icon: Users, color: '#0066CC' },
                  { label: 'Unique Visitors (30d)', value: phData.unique_visitors_30d || 0, icon: Globe, color: '#14b8a6' },
                  { label: 'Page Views (7d)', value: phData.pageviews_7d || 0, icon: MousePointer, color: '#8b5cf6' },
                  { label: 'Page Views (30d)', value: phData.pageviews_30d || 0, icon: TrendingUp, color: '#f59e0b' },
                ].map(m => (
                  <div key={m.label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`ph-metric-${m.label}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${m.color}15` }}>
                        <m.icon className="w-4 h-4" style={{ color: m.color }} />
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-taxera-charcoal">{m.value.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{m.label}</p>
                  </div>
                ))}
              </div>

              {/* Daily trend */}
              {phData.daily_trend && phData.daily_trend.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-5">
                  <h3 className="text-sm font-bold text-taxera-charcoal mb-4">Daily Traffic (30 days)</h3>
                  <div className="flex items-end gap-1 h-32">
                    {phData.daily_trend.map((d, i) => {
                      const maxViews = Math.max(...phData.daily_trend.map(x => x.views), 1);
                      const pct = (d.views / maxViews) * 100;
                      return (
                        <div key={d.date} className="flex-1 group relative" data-testid={`ph-bar-${i}`}>
                          <div className="bg-taxera-blue/20 hover:bg-taxera-blue/40 rounded-t transition-colors" style={{ height: `${Math.max(pct, 2)}%` }}>
                            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-slate-800 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">
                              {d.date}: {d.views} views / {d.visitors} visitors
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Top pages */}
                {phData.top_pages && phData.top_pages.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-sm font-bold text-taxera-charcoal mb-3">Top Pages</h3>
                    <div className="space-y-2">
                      {phData.top_pages.map((p, i) => (
                        <div key={`page-${p.page}`} className="flex items-center justify-between text-xs" data-testid={`ph-page-${i}`}>
                          <span className="text-slate-600 truncate max-w-[250px]">{p.page || '/'}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-slate-400">{p.unique_visitors} visitors</span>
                            <span className="font-semibold text-taxera-charcoal">{p.views} views</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top referrers */}
                {phData.top_referrers && phData.top_referrers.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-5">
                    <h3 className="text-sm font-bold text-taxera-charcoal mb-3">Traffic Sources</h3>
                    <div className="space-y-2">
                      {phData.top_referrers.map((r, i) => (
                        <div key={`ref-${r.referrer}`} className="flex items-center justify-between text-xs" data-testid={`ph-ref-${i}`}>
                          <span className="text-slate-600 truncate max-w-[250px]">{r.referrer || 'Direct'}</span>
                          <span className="font-semibold text-taxera-charcoal">{r.views} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {view === 'overview' && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {metricCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`analytics-card-${card.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${card.color}12` }}>
                      <Icon className="w-4 h-4" style={{ color: card.color }} strokeWidth={1.5} />
                    </div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wide">{card.label}</span>
                  </div>
                  <div className="font-heading text-3xl font-bold text-taxera-charcoal">{typeof card.value === 'number' && card.value % 1 !== 0 ? card.value : card.value.toLocaleString()}</div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-taxera-charcoal">Top Pages (Last 30 days)</h3>
            </div>
            <div className="divide-y divide-slate-50">
              {data.top_pages.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-8">No page view data yet. Analytics will populate as visitors browse the site.</p>
              ) : (
                data.top_pages.map((p, i) => {
                  const maxViews = data.top_pages[0]?.views || 1;
                  return (
                    <div key={p.page} className="px-5 py-3 flex items-center gap-4" data-testid={`top-page-${i}`}>
                      <span className="text-xs font-bold text-slate-400 w-6">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-taxera-charcoal truncate block">{p.page}</span>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-taxera-teal rounded-full" style={{ width: `${(p.views / maxViews) * 100}%` }} />
                        </div>
                      </div>
                      <span className="text-sm font-bold text-slate-500 shrink-0">{p.views.toLocaleString()}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {data.daily_views.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-bold text-taxera-charcoal mb-4">Daily Page Views (Last 30 days)</h3>
              <div className="flex items-end gap-[2px] h-32">
                {data.daily_views.map((d) => {
                  const maxDv = Math.max(...data.daily_views.map(x => x.views), 1);
                  const height = (d.views / maxDv) * 100;
                  return (
                    <div key={d.date} className="flex-1 min-w-0 group relative" title={`${d.date}: ${d.views} views`}>
                      <div className="bg-taxera-teal/80 hover:bg-taxera-teal rounded-t transition-colors" style={{ height: `${Math.max(height, 2)}%` }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-2xs text-slate-400">{data.daily_views[0]?.date}</span>
                <span className="text-2xs text-slate-400">{data.daily_views[data.daily_views.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {data.lead_sources.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <h3 className="text-sm font-bold text-taxera-charcoal mb-4">Lead Sources</h3>
              <div className="space-y-2">
                {data.lead_sources.map((l) => (
                  <div key={l.source} className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 capitalize">{l.source}</span>
                    <span className="text-sm font-bold text-taxera-charcoal">{l.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {view === 'funnel' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-taxera-charcoal mb-6">Conversion Funnel</h3>
          <div className="space-y-3">
            {funnelSteps.map((step, i) => {
              const widthPct = maxFunnel > 0 ? Math.max((step.value / maxFunnel) * 100, 8) : 8;
              const convRate = i > 0 && funnelSteps[i - 1].value > 0
                ? Math.round((step.value / funnelSteps[i - 1].value) * 100) : null;
              return (
                <div key={step.label} data-testid={`funnel-step-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-600">{step.label}</span>
                    <div className="flex items-center gap-3">
                      {convRate !== null && <span className="text-2xs font-medium text-slate-400">{convRate}% conv.</span>}
                      <span className="text-sm font-bold text-taxera-charcoal">{step.value.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="h-8 bg-slate-50 rounded-lg overflow-hidden">
                    <div className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-3"
                      style={{ width: `${widthPct}%`, backgroundColor: step.color }}>
                      {widthPct > 20 && <span className="text-xs font-bold text-white">{step.value}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 p-4 bg-slate-50 rounded-xl">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Quick Stats</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-lg font-bold text-taxera-charcoal">{funnel.newsletter || 0}</div>
                <div className="text-2xs text-slate-400">Newsletter subs</div>
              </div>
              <div>
                <div className="text-lg font-bold text-taxera-charcoal">{funnel.registered_users || 0}</div>
                <div className="text-2xs text-slate-400">Total users</div>
              </div>
              <div>
                <div className="text-lg font-bold text-taxera-charcoal">{(funnel.leads || 0) + (funnel.business_cases || 0)}</div>
                <div className="text-2xs text-slate-400">Total leads</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {view === 'heatmap' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-sm font-bold text-taxera-charcoal mb-2">Traffic Heatmap (Last 7 Days)</h3>
          <p className="text-xs text-slate-400 mb-4">Shows when your visitors are most active. Darker = more traffic.</p>

          {heatmapData.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">Not enough traffic data yet to generate a heatmap. Data will appear as visitors browse the site.</p>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                <div className="flex mb-1">
                  <div className="w-20 shrink-0" />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div key={h} className="flex-1 text-center text-2xs text-slate-400 font-medium">
                      {h.toString().padStart(2, '0')}
                    </div>
                  ))}
                </div>
                {uniqueDays.map(day => {
                  const dayLabel = new Date(day + 'T00:00:00').toLocaleDateString('en', { weekday: 'short', month: 'short', day: 'numeric' });
                  return (
                    <div key={day} className="flex items-center mb-1">
                      <div className="w-20 shrink-0 text-2xs text-slate-500 font-medium truncate pr-2">{dayLabel}</div>
                      {Array.from({ length: 24 }, (_, h) => {
                        const cell = heatmapData.find(d => d.day === day && d.hour === h);
                        const count = cell?.count || 0;
                        const intensity = count / maxHeatCount;
                        const bg = count === 0 ? '#f8fafc' : `rgba(20, 184, 166, ${0.15 + intensity * 0.85})`;
                        return (
                          <div key={h} className="flex-1 aspect-square rounded-sm mx-[1px]"
                            style={{ backgroundColor: bg }}
                            title={`${dayLabel} ${h}:00 - ${count} views`} />
                        );
                      })}
                    </div>
                  );
                })}
                <div className="flex items-center justify-end gap-2 mt-3">
                  <span className="text-2xs text-slate-400">Less</span>
                  {[0, 0.25, 0.5, 0.75, 1].map((intensity, i) => (
                    <div key={`legend-${i}`} className="w-3 h-3 rounded-sm"
                      style={{ backgroundColor: intensity === 0 ? '#f8fafc' : `rgba(20, 184, 166, ${0.15 + intensity * 0.85})` }} />
                  ))}
                  <span className="text-2xs text-slate-400">More</span>
                </div>
              </div>
            </div>
          )}

          {data.user_growth && data.user_growth.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-100">
              <h3 className="text-sm font-bold text-taxera-charcoal mb-4">User Signups (Last 30 days)</h3>
              <div className="flex items-end gap-1 h-24">
                {data.user_growth.map((d) => {
                  const maxSignups = Math.max(...data.user_growth.map(x => x.signups), 1);
                  const height = (d.signups / maxSignups) * 100;
                  return (
                    <div key={d.date} className="flex-1 min-w-0 group relative" title={`${d.date}: ${d.signups} signups`}>
                      <div className="bg-blue-500/80 hover:bg-blue-500 rounded-t transition-colors" style={{ height: `${Math.max(height, 4)}%` }} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsTab;
